import { ReactNode, useMemo } from "react";

import styled from "@emotion/styled";

import { Box, BoxProps, Flex } from "@/components/layout/flexbox";
import { ProfileIntakeForm } from "@/types/users";
import { NonNullableArrayItem } from "@/utils/types";

const itemRenderer = (item: string) => {
  return item && item.replace("_", " ").toLowerCase();
};

type MatchingCategory = NonNullableArrayItem<
  NonNullableArrayItem<
    ProfileIntakeForm["matchingOptions"]
  >["matchingCategories"]
>;

type MatchingCategoryDetail = MatchingCategory & {
  options: ProfileIntakeForm["matchingOptions"];
};

function buildMatchingCategoryDetails(
  matchingOptions: ProfileIntakeForm["matchingOptions"],
): Record<string, MatchingCategoryDetail> {
  if (!matchingOptions) return {};
  return matchingOptions
    .filter((option) => option.visible)
    .reduce<Record<string, MatchingCategoryDetail>>((res, option) => {
      const categories = option.matchingCategories || [];
      categories.forEach((category) => {
        const categoryDetail = res[category.id] || { ...category, options: [] };
        categoryDetail.options.push(option);
        res[category.id] = categoryDetail;
      });
      return res;
    }, {});
}

export function ClientIntakeForm({
  intakeForm,
}: {
  intakeForm: ProfileIntakeForm;
}) {
  const {
    matchingOptions,
    industriesSkipped,
    jobFunctionsSkipped,
    industries,
    jobFunctions,
  } = intakeForm;

  const matchingCategoryDetails = useMemo(
    () => buildMatchingCategoryDetails(matchingOptions),
    [matchingOptions],
  );

  return (
    <>
      {Object.entries(matchingCategoryDetails).map(([id, categoryDetail]) => (
        <Row key={id}>
          <Label>{categoryDetail.name}</Label>
          <OverflowBox>
            <ItemList>
              {categoryDetail.options.map((option) => (
                <li key={option.id}>{option.name}</li>
              ))}
            </ItemList>
          </OverflowBox>
        </Row>
      ))}
      <Row>
        <Label>Industries</Label>
        <OverflowBox>
          <ItemList>
            {industriesSkipped ? (
              <li>Not provided</li>
            ) : (
              industries.map((industry) => (
                <li key={industry.id}>{itemRenderer(industry.name)}</li>
              ))
            )}
          </ItemList>
        </OverflowBox>
      </Row>
      <Row>
        <Label>Job Functions</Label>
        <OverflowBox>
          <ItemList>
            {jobFunctionsSkipped ? (
              <li>Not provided</li>
            ) : (
              jobFunctions.map((jobFunction) => (
                <li key={jobFunction.id}>{itemRenderer(jobFunction.name)}</li>
              ))
            )}
          </ItemList>
        </OverflowBox>
      </Row>
    </>
  );
}

function Row(props: BoxProps & { children: ReactNode }) {
  return <Flex py={1} {...props} />;
}

interface OverflowBoxProps extends BoxProps {
  children: ReactNode;
}

function OverflowBox(props: OverflowBoxProps) {
  return (
    <Box
      width={1}
      style={{
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
      }}
      {...props}
    />
  );
}

const Label = styled(Box)`
  margin-right: 24px;
  font-weight: 600;
  width: 205px;
`;

const ItemList = styled("ul")`
  text-transform: capitalize;
  margin-left: 0px;
  padding-left: 20px;
`;
